import type { GameInput, RuleContext, RuleOutcome, RuleSet, StepResult } from '../rules/contracts';
import { getRuleSet } from '../rules/registry';
import { createRng, type SeededRng } from '../rng';
import type {
  GameModeId,
  Json,
  MatchEndReason,
  MatchOutcome,
  MatchResult,
  SeatIndex,
  SeatResult,
  Timestamp,
} from '../types';
import { EMPTY_STEP, type GameMode, type ModeSetup, type ModeStep } from './GameMode';

/**
 * Shared plumbing for every mode: resolving the ruleset, seeding the RNG,
 * building the `RuleContext`, holding the current state, turning a terminal
 * state into a `MatchResult`.
 *
 * None of that is a game rule, which is why it lives here instead of being
 * copy-pasted into each mode. What a concrete mode overrides is only what is
 * genuinely mode-specific:
 *
 *   - `canAct`          - who may send input right now
 *   - `outcomeForSeat`  - what winning means with this many seats
 *   - `afterStep`       - cross-seat reactions (attacks, shared board, ...)
 */
export abstract class BaseGameMode implements GameMode {
  abstract readonly id: GameModeId;
  abstract readonly requiredSeats: number;
  abstract readonly roundDurationMs: number | null;

  readonly ruleSetId: string;

  private resolvedRuleSet: RuleSet<unknown, unknown> | null = null;

  /**
   * Resolved on first use, not in the constructor: a mode may be constructed
   * before `bootstrapShared()` has filled the registry, and failing at that
   * point would only obscure the real ordering problem.
   */
  protected get ruleSet(): RuleSet<unknown, unknown> {
    this.resolvedRuleSet ??= getRuleSet(this.ruleSetId);
    return this.resolvedRuleSet;
  }

  protected setup!: ModeSetup;
  protected state: unknown;
  protected rng!: SeededRng;
  protected finished = false;
  protected endedReason: MatchEndReason = 'completed';
  protected forcedLoserSeat: SeatIndex | null = null;
  /** Monotonic, so the renderer can drop out-of-order batches. */
  protected stepSeq = 0;

  constructor(ruleSetId: string) {
    this.ruleSetId = ruleSetId;
  }

  // --- lifecycle -------------------------------------------------------------

  init(setup: ModeSetup): void {
    this.setup = setup;
    this.rng = createRng(setup.seed);
    this.finished = false;
    this.endedReason = 'completed';
    this.forcedLoserSeat = null;
    this.stepSeq = 0;
    this.state = this.ruleSet.createInitialState({
      seed: setup.seed,
      seats: setup.seats.length,
      config: setup.config,
    });
  }

  handleInput(input: GameInput, now: Timestamp): ModeStep {
    if (this.finished) {
      return {
        ...EMPTY_STEP,
        seat: input.seat,
        rejected: { code: 'out-of-phase', message: 'Match is over.' },
      };
    }

    const denial = this.canAct(input.seat, now);
    if (denial) return { ...EMPTY_STEP, seat: input.seat, rejected: denial };

    const ctx = this.context(now);

    const invalid = this.ruleSet.validate?.(this.state, input, ctx);
    if (invalid) return { ...EMPTY_STEP, seat: input.seat, rejected: invalid };

    return { ...this.commit(this.ruleSet.reduce(this.state, input, ctx), now), seat: input.seat };
  }

  tick(now: Timestamp): ModeStep | null {
    if (this.finished || !this.ruleSet.tick) return null;
    const result = this.ruleSet.tick(this.state, this.context(now));
    return result ? this.commit(result, now) : null;
  }

  isFinished(): boolean {
    return this.finished || this.ruleSet.isTerminal(this.state);
  }

  /** Ends the match without the rules asking: forfeit, disconnect, timeout. */
  forceFinish(reason: MatchEndReason, losingSeat?: SeatIndex): void {
    this.finished = true;
    this.endedReason = reason;
    this.forcedLoserSeat = losingSeat ?? null;
  }

  result(endedAt: Timestamp, reason: MatchEndReason): MatchResult {
    const outcome = this.ruleSet.summarize(this.state);
    return {
      matchId: this.setup.matchId,
      modeId: this.id,
      ruleSetId: this.ruleSet.id,
      ruleSetVersion: this.ruleSet.version,
      seed: this.setup.seed,
      startedAt: this.setup.startedAt,
      endedAt,
      reason: this.finished ? this.endedReason : reason,
      seats: this.setup.seats.map((seat) => this.seatResult(seat.seat, outcome)),
    };
  }

  // --- persistence -----------------------------------------------------------

  snapshot(): Json {
    return {
      stepSeq: this.stepSeq,
      finished: this.finished,
      endedReason: this.endedReason,
      forcedLoserSeat: this.forcedLoserSeat,
      rngState: this.rng.state(),
      state: this.stateSnapshot(),
    };
  }

  stateSnapshot(): Json {
    return (this.ruleSet.serialize?.(this.state) ?? this.state) as Json;
  }

  restore(snapshot: Json): void {
    const raw = snapshot as {
      stepSeq?: number;
      finished?: boolean;
      endedReason?: MatchEndReason;
      forcedLoserSeat?: SeatIndex | null;
      rngState?: number;
      state?: Json;
    };
    this.stepSeq = raw.stepSeq ?? 0;
    this.finished = raw.finished ?? false;
    this.endedReason = raw.endedReason ?? 'completed';
    this.forcedLoserSeat = raw.forcedLoserSeat ?? null;
    if (typeof raw.rngState === 'number') this.rng.restore(raw.rngState);
    this.state = this.ruleSet.deserialize?.(raw.state as Json) ?? raw.state;
  }

  // --- hooks a concrete mode overrides ---------------------------------------

  /**
   * May this seat act right now? Return a rejection to refuse.
   * Default: any seated player, any time the match is running.
   */
  protected canAct(_seat: SeatIndex, _now: Timestamp): ModeStep['rejected'] {
    return undefined;
  }

  /**
   * Cross-seat consequences of a step - the hook a versus mode grows into.
   * Return extra transitions/events to append, or nothing.
   */
  protected afterStep(_step: StepResult<unknown>, _now: Timestamp): ModeStep | null {
    return null;
  }

  /** What this seat's result is called, given the ruleset's verdict. */
  protected outcomeForSeat(seat: SeatIndex, outcome: RuleOutcome): MatchOutcome {
    if (outcome.winners.length === 0) return 'draw';
    return outcome.winners.includes(seat) ? 'win' : 'loss';
  }

  // --- internals -------------------------------------------------------------

  protected context(now: Timestamp): RuleContext {
    return {
      now,
      elapsed: now - this.setup.startedAt,
      random: this.rng,
      authoritative: this.setup.authoritative,
    };
  }

  private commit(result: StepResult<unknown>, now: Timestamp): ModeStep {
    if (result.rejected) {
      return {
        transitions: result.transitions,
        events: result.events,
        rejected: result.rejected,
        finished: this.finished,
      };
    }

    this.state = result.state;
    this.stepSeq += 1;
    if (result.terminal) this.finished = true;

    const extra = this.afterStep(result, now);

    return {
      transitions: extra ? [...result.transitions, ...extra.transitions] : result.transitions,
      events: extra ? [...result.events, ...extra.events] : result.events,
      finished: this.finished,
    };
  }

  private seatResult(seat: SeatIndex, outcome: RuleOutcome): SeatResult {
    const entry = this.setup.seats.find((s) => s.seat === seat);
    const forcedOutcome =
      this.forcedLoserSeat === null
        ? null
        : seat === this.forcedLoserSeat
          ? this.endedReason === 'disconnect'
            ? 'abandoned'
            : 'loss'
          : 'win';
    return {
      seat,
      playerId: entry?.playerId ?? null,
      outcome: forcedOutcome ?? this.outcomeForSeat(seat, outcome),
      score: outcome.scores[seat] ?? 0,
      stats: outcome.stats[seat] ?? {},
    };
  }
}
